import { Ingredient } from "@/lib/types";
import { Badge } from "../ui/badge";
import { ExternalLinkIcon, UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type SourceBadgeProps = {
  source: Ingredient["source"];
  externalId: Ingredient["externalId"];
  link?: boolean;
  size?: "sm" | "default";
};

export const SOURCE_TO_LABEL = {
  usda_foundation: "USDA Foundation",
  usda_fndds: "USDA FNDDS",
  manual: "Built-in",
  usda_branded: "USDA Branded",
  usda_sr_legacy: "USDA SR Legacy",
  ifct: "IFCT 2017",
  user: "Custom",
};

export function sourceLabel(source: Ingredient["source"]): string {
  switch (source) {
    case "usda_foundation":
    case "usda_fndds":
    case "manual":
    case "usda_branded":
    case "usda_sr_legacy":
    case "ifct":
    case "user":
      return SOURCE_TO_LABEL[source];
    default:
      return source ?? "";
  }
}

function getSourceLink(
  source: Ingredient["source"],
  externalId: Ingredient["externalId"],
) {
  if (source && source.includes("usda")) {
    return `https://fdc.nal.usda.gov/food-details/${externalId}/nutrients`;
  }

  if (source === "ifct" && externalId) {
    return `https://ifct2017.github.io/data/compositions?code=${encodeURIComponent(externalId)}`;
  }

  return null;
}

export function SourceBadge({
  source,
  externalId,
  link = true,
  size = "default",
}: SourceBadgeProps) {
  const sourceLink = getSourceLink(source, externalId);
  const shouldLink = sourceLink && link;

  const badge = (
    <Badge
      variant="outline"
      size={size}
      className={cn(shouldLink && "hover:underline")}
    >
      {source === "user" ? (
        <UserIcon className="mr-1 h-3 w-3" aria-hidden />
      ) : null}
      {sourceLabel(source)}
      {shouldLink && <ExternalLinkIcon className="ml-1 h-3 w-3" aria-hidden />}
    </Badge>
  );

  if (!shouldLink) {
    return badge;
  }

  return (
    <a
      href={sourceLink}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:underline"
    >
      {badge}
    </a>
  );
}
